import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // Thêm dòng import này
import { FaUser, FaEnvelope, FaLock, FaCheckCircle, FaExclamationTriangle, FaEye, FaEyeSlash, FaTag } from 'react-icons/fa';
import SEO from '../components/SEO';

const Register = () => {
  const location = useLocation();
  const [referralCode, setReferralCode] = useState(null);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refer = params.get('refer');
    if (refer) {
      setReferralCode(refer);
    }
  }, [location]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'password') {
      checkPasswordStrength(value);
    }
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 1;
    
    setPasswordStrength(strength);
  };
  
  const getPasswordStrengthClass = () => {
    if (passwordStrength === 0) return 'bg-gray-300 w-0';
    if (passwordStrength === 1) return 'bg-red-500 w-1/4';
    if (passwordStrength === 2) return 'bg-orange-500 w-2/4';
    if (passwordStrength === 3) return 'bg-yellow-500 w-3/4';
    if (passwordStrength === 4) return 'bg-green-500 w-full';
  };
  
  const getPasswordStrengthText = () => {
    if (passwordStrength === 0) return '';
    if (passwordStrength === 1) return 'Yếu';
    if (passwordStrength === 2) return 'Trung bình';
    if (passwordStrength === 3) return 'Khá';
    if (passwordStrength === 4) return 'Mạnh';
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Tên người dùng không được để trống';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Tên người dùng phải có ít nhất 3 ký tự';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email không được để trống';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email không hợp lệ';
    }
    
    if (!formData.password) {
      newErrors.password = 'Mật khẩu không được để trống';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Mật khẩu phải có ít nhất 6 ký tự';
    } else if (passwordStrength < 2) {
      newErrors.password = 'Mật khẩu quá yếu, vui lòng sử dụng mật khẩu mạnh hơn';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Mật khẩu nhập lại không khớp';
    }
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Lưu ý: không cần chỉ định domain và port vì API được phục vụ từ cùng một nguồn
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          referralCode: referralCode
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitStatus('success');
        
        setFormData({
          username: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
        setPasswordStrength(0);
      } else {
        // Xử lý lỗi cụ thể từ backend
        if (data.field) {
          setErrors({
            [data.field]: data.message
          });
        } else {
          setSubmitStatus('error');
        }
      }
    } catch (error) {
      console.error('Lỗi đăng ký:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <>
      <SEO 
        title="Đăng ký tài khoản PSA Roleplay"
        description="Đăng ký tài khoản để tham gia máy chủ PSA Online của chúng tôi"
      />
      
      <Helmet>
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Đăng ký tài khoản PSA Roleplay",
          "description": "Đăng ký tài khoản để tham gia máy chủ PSA Online của chúng tôi",
          "url": "https://psa.world/register"
        })}
        </script>

        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Trang chủ",
              "item": "https://psa.world"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Đăng ký tài khoản PSA Roleplay",
              "item": "https://psa.world/register"
            }
          ]
        })}
        </script>
      </Helmet>

      <div className="min-h-screen pt-24 pb-16 relative">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/resources/images/gta-bg.png)`
          }}
        ></div>
        
        <div className="absolute inset-0 bg-grid-pattern opacity-10 z-0"></div>
        
        <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-accent-teal/20 rounded-full filter blur-[100px] -z-10 animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-accent/10 rounded-full filter blur-[120px] -z-10 animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <motion.div 
              className="glassmorphism-card w-full max-w-md p-8 relative z-10 register-form"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div
                className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-l from-accent/20 to-transparent -z-1"
                style={{ opacity: 0.5 }}
              />
              
              <motion.div 
                className="absolute bottom-0 left-0 w-12 h-12 bg-gradient-to-r from-accent/20 to-transparent -z-1"
                style={{ opacity: 0.5 }}
              />
              
              <motion.h1 
                className="text-2xl md:text-3xl font-bold mb-2 text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <span className="text-gradient-teal">Đăng Ký Tài Khoản</span>
              </motion.h1>
              
              <motion.p 
                className="text-light/70 mb-6 text-center text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                Tham gia ngay để trải nghiệm PSA Online
              </motion.p>
              
              {submitStatus === 'success' && (
                <motion.div 
                  className="bg-green-500/20 border border-green-500/30 text-green-400 p-3 rounded-lg mb-4 flex items-center"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FaCheckCircle className="mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm">Đăng ký thành công! Vui lòng kiểm tra email để xác nhận tài khoản.</p>
                  </div>
                </motion.div>
              )}
              {submitStatus === 'error' && (
                <motion.div 
                  className="bg-red-500/20 border border-red-500/30 text-red-400 p-3 rounded-lg mb-4 flex items-center"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FaExclamationTriangle className="mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm">Đăng ký thất bại! Đã có lỗi xảy ra. Vui lòng thử lại sau.</p>
                  </div>
                </motion.div>
              )}
              
              <form onSubmit={handleSubmit}>
                <motion.div 
                  className="mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  <label className="block text-light mb-1 font-medium text-sm" htmlFor="username">
                    Tên người dùng
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-light/50 group-hover:text-accent-teal transition-colors">
                      <FaUser />
                    </div>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      className={`w-full pl-10 pr-4 py-2.5 rounded-lg bg-dark/30 backdrop-blur-md border ${errors.username ? 'border-red-400' : 'border-white/10'} focus:border-accent-teal outline-none transition-colors text-sm`}
                      placeholder="Nhập tên người dùng"
                      value={formData.username}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.username && (
                    <motion.p 
                      className="text-red-400 text-xs mt-1 flex items-center"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      <FaExclamationTriangle className="mr-1" size={10} />
                      {errors.username}
                    </motion.p>
                  )}
                </motion.div>
                
                <motion.div 
                  className="mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  <label className="block text-light mb-1 font-medium text-sm" htmlFor="email">
                    Email
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-light/50 group-hover:text-accent-teal transition-colors">
                      <FaEnvelope />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={`w-full pl-10 pr-4 py-2.5 rounded-lg bg-dark/30 backdrop-blur-md border ${errors.email ? 'border-red-400' : 'border-white/10'} focus:border-accent-teal outline-none transition-colors text-sm`}
                      placeholder="Nhập địa chỉ email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.email && (
                    <motion.p 
                      className="text-red-400 text-xs mt-1 flex items-center"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      <FaExclamationTriangle className="mr-1" size={10} />
                      {errors.email}
                    </motion.p>
                  )}
                </motion.div>
                
                <motion.div 
                  className="mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.3 }}
                >
                  <label className="block text-light mb-1 font-medium text-sm" htmlFor="password">
                    Mật khẩu
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-light/50 group-hover:text-accent-teal transition-colors">
                      <FaLock />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      className={`w-full pl-10 pr-12 py-2.5 rounded-lg bg-dark/30 backdrop-blur-md border ${errors.password ? 'border-red-400' : 'border-white/10'} focus:border-accent-teal outline-none transition-colors text-sm`}
                      placeholder="Tạo mật khẩu mạnh"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <button 
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-light/50 hover:text-light"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                    </button>
                  </div>
                  
                  {formData.password && (
                    <div className="mt-1.5">
                      <div className="h-1 w-full bg-gray-200/20 rounded-full overflow-hidden">
                        <motion.div 
                          className={`h-full ${getPasswordStrengthClass()}`}
                          initial={{ width: 0 }}
                          animate={{ width: passwordStrength === 0 ? '0%' : passwordStrength === 1 ? '25%' : passwordStrength === 2 ? '50%' : passwordStrength === 3 ? '75%' : '100%' }}
                          transition={{ duration: 0.5 }}
                        ></motion.div>
                      </div>
                      <div className="flex justify-between mt-0.5">
                        <span className="text-xs text-light/60">Độ mạnh:</span>
                        <span className={`text-xs ${
                          passwordStrength === 0 ? 'text-light/60' : 
                          passwordStrength === 1 ? 'text-red-500' : 
                          passwordStrength === 2 ? 'text-orange-500' : 
                          passwordStrength === 3 ? 'text-yellow-500' : 
                          'text-green-500'
                        }`}>{getPasswordStrengthText()}</span>
                      </div>
                    </div>
                  )}
                  
                  {errors.password && (
                    <motion.p 
                      className="text-red-400 text-xs mt-1 flex items-center"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      <FaExclamationTriangle className="mr-1" size={10} />
                      {errors.password}
                    </motion.p>
                  )}
                </motion.div>
                
                <motion.div 
                  className="mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.3 }}
                >
                  <label className="block text-light mb-1 font-medium text-sm" htmlFor="confirmPassword">
                    Xác nhận mật khẩu
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-light/50 group-hover:text-accent-teal transition-colors">
                      <FaLock />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      className={`w-full pl-10 pr-12 py-2.5 rounded-lg bg-dark/30 backdrop-blur-md border ${errors.confirmPassword ? 'border-red-400' : 'border-white/10'} focus:border-accent-teal outline-none transition-colors text-sm`}
                      placeholder="Nhập lại mật khẩu"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                    <button 
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-light/50 hover:text-light"
                      onClick={toggleConfirmPasswordVisibility}
                    >
                      {showConfirmPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <motion.p 
                      className="text-red-400 text-xs mt-1 flex items-center"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      <FaExclamationTriangle className="mr-1" size={10} />
                      {errors.confirmPassword}
                    </motion.p>
                  )}
                </motion.div>
                
                {referralCode && (
                  <motion.div 
                    className="mb-4"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.3 }}
                  >
                    <label className="block text-light mb-1 font-medium text-sm" htmlFor="referralCode">
                      Mã giới thiệu
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-light/50 group-hover:text-accent-teal transition-colors">
                        <FaTag />
                      </div>
                      <input
                        type="text"
                        id="referralCode"
                        name="referralCode"
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-dark/30 backdrop-blur-md border border-white/10 outline-none transition-colors text-sm cursor-not-allowed"
                        value={referralCode}
                        disabled
                        readOnly
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-accent-teal/20 text-accent-teal text-xs py-0.5 px-2 rounded">
                        Đã xác nhận
                      </div>
                    </div>
                    <p className="text-xs text-accent-teal/80 mt-1">
                      Bạn đang sử dụng mã giới thiệu từ người chơi khác, bạn sẽ nhận được thêm phần thưởng mỗi khi lên cấp độ.
                    </p>
                  </motion.div>
                )}
                
                <motion.div
                  className="mt-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.3 }}
                >
                  <button 
                    type="submit"
                    className="btn btn-primary py-3 w-full flex justify-center items-center rounded-lg font-medium text-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Đang xử lý...
                      </span>
                    ) : (
                      'ĐĂNG KÝ NGAY'
                    )}
                  </button>
                </motion.div>
              </form>
              
              <div className="mt-5 pt-5 border-t border-white/5">
                <p className="text-light/50 text-xs text-center">
                  Bằng cách đăng ký, bạn đồng ý với <Link to="/dieu-khoan" className="text-accent-teal hover:underline">Điều khoản</Link> và <Link to="/chinh-sach" className="text-accent-teal hover:underline">Chính sách</Link> của chúng tôi.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
